/**
 * DOM manipulation utilities
 */

/**
 * Gets query parameters from the current URL
 */
export const getQueryParams = (): Record<string, string> => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  return Object.fromEntries(urlSearchParams.entries());
};

/**
 * Sets a query parameter in the URL
 */
export const setQueryParams = (key: string, value: string): URLSearchParams => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  urlSearchParams.set(key, value);
  return urlSearchParams;
};

/**
 * Wait/sleep for a specified number of milliseconds
 */
export const wait = (n: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, n));

/**
 * Sleep function (alias for wait)
 */
export function sleep(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

/**
 * Checks if a path should be excluded from being saved as previous route
 */
export const shouldExcludeFromPrevRoute = (path: string): boolean => {
  const excludedPaths = ['/set-password', '/login', '/forgot-password', '/service-unavailable'];
  return excludedPaths.some((excludedPath) => path.includes(excludedPath));
};

/**
 * Safely sets previous route in localStorage if path is not excluded
 */
export const setPrevRouteIfAllowed = (path: string): void => {
  if (!shouldExcludeFromPrevRoute(path)) {
    localStorage.setItem('prevRoute', path);
  }
};

/**
 * Checks internet connectivity
 */
export async function checkInternetConnectivity(): Promise<boolean> {
  // Check if navigator.onLine is available and returns true
  const navigatorOnline = 'onLine' in navigator ? navigator.onLine : null;

  // If navigator explicitly says we're online, trust it
  if (navigatorOnline === true) {
    return true;
  }

  // If navigator says offline or is not available, use fallback API check
  try {
    // Import api dynamically to avoid circular dependency
    const { default: api } = await import('../api');

    // Try to call the preference API with a short timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

    console.log('internet check fallback initiated ');

    const response = await api.get('settings/preferences/?internetCheck=true', {
      signal: controller.signal,
    });
    console.log(
      'internet check fallback response :',
      response.status >= 200 && response.status < 300
    );

    clearTimeout(timeoutId);

    // If we get a successful response, we have internet connectivity
    return response.status >= 200 && response.status < 300;
  } catch (error: unknown) {
    const err = error as { code?: string; message?: string; name?: string };
    // If the error is due to network issues, we don't have connectivity
    if (
      err.code === 'ERR_NETWORK' ||
      err.code === 'ERR_INTERNET_DISCONNECTED' ||
      err.message === 'Network Error' ||
      err.name === 'AbortError'
    ) {
      console.log('internet check fallback catch (if) :', err?.code || err?.message, false);

      return false;
    }

    // For other errors (like 401, 403, etc.), we still have internet connectivity
    // just the API call failed for other reasons
    console.log('internet check fallback catch : ', error, true);
    return true;
  }
}

/**
 * Gets value from form event
 */
export const getValueFromEvent = (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | unknown,
  inputType?: string,
  convert: boolean = false
): string | boolean | number | unknown => {
  const event = e as {
    target?: { type?: string; checked?: boolean; name?: string; value?: string };
  };
  let value: string | boolean | number | unknown = '';

  if (event?.target?.type === 'checkbox') {
    value = event?.target?.checked;
  } else if (event?.target?.type === 'radio') {
    value = +(event?.target?.name || 0);
  } else if (['textarea'].includes(inputType || '')) {
    value = event?.target?.value;
  } else if (['dropdown', 'multiselect', 'multiselect-virtual'].includes(inputType || '')) {
    value = e;
  } else {
    if (convert) {
      value = (event?.target?.value || '')?.toString()?.trim().toLowerCase();
    } else {
      value = (event?.target?.value || '')?.toString()?.trim();
    }
  }
  return value;
};
